import { useMemo, useRef, useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { getRandomSaintId } from "../services/saints";
import CountryFlag from "../components/CountryFlag";
import { Button } from "../components/ui/Button";
import { Card, CardBody } from "../components/ui/Card";
import duliaLogoGold from "../components/Dulia2Gold.png";
import duliaLogoNavy from "../components/Dulia2Navy.png";
import goldTexture from "../components/GoldTexture.jpg";
import {
  FaSearch,
  FaDiceThree,
  FaCalendarAlt,
  FaSyncAlt,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { useAuth } from "../hooks/useAuth";

/** --- tiny helpers (kept small) --- */
function InfoPill({ children, tone = "default", className = "" }) {
  const tones = {
    default:
      "border-dulia-line bg-white/90 text-dulia-ink dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment",
    muted:
      "border-dulia-line bg-dulia-parchment/80 text-dulia-navy dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment/90",
    accent:
      "border-dulia-gold/50 bg-dulia-gold/10 text-dulia-navy dark:border-dulia-gold/40 dark:bg-dulia-gold/15 dark:text-dulia-goldSoft",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${tones[tone] || tones.default} ${className}`}
    >
      {children}
    </span>
  );
}

function ActionTile({ title, description, children }) {
  return (
    <Card className="h-full rounded-[24px] border-dulia-line bg-dulia-panel shadow-soft dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft">
      <CardBody className="flex h-full flex-col gap-4 p-5">
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-dulia-ink dark:text-dulia-parchment">
            {title}
          </h3>
          <p className="text-sm leading-6 text-dulia-navy/75 dark:text-dulia-parchment/75">
            {description}
          </p>
        </div>
        <div className="pt-1">{children}</div>
      </CardBody>
    </Card>
  );
}

function RandomNavButton({
  className = "",
  children,
  to = "/random",
  title,
  style,
}) {
  const navigate = useNavigate();
  const [rolling, setRolling] = useState(false);
  const [diceRotation, setDiceRotation] = useState(0);

  function handleClick() {
    if (rolling) return;

    setRolling(true);
    setDiceRotation((prev) => prev + 360);

    window.setTimeout(() => {
      navigate(to);
      setRolling(false);
    }, 450);
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={className}
      style={style}
      title={title}
      disabled={rolling}
    >
      <FaDiceThree
        className="transition-transform duration-500"
        style={{ transform: `rotate(${diceRotation}deg)` }}
        aria-hidden="true"
      />
      {children}
    </button>
  );
}

function parseFeastKey(key) {
  if (!key) return null;
  const s = String(key).trim();
  if (!/^\d{2}-\d{2}$/.test(s)) return null;
  const m = Number(s.slice(0, 2));
  const d = Number(s.slice(3, 5));
  if (!m || !d) return null;
  return { month: m, day: d, key: s };
}

function stageLabel(saint) {
  if (!saint) return "";
  const stage = String(saint.stage || "").trim();
  if (!stage) return "";
  if (String(saint.name || "").includes("Martyrs")) return "";
  if (saint.isPope) return `Pope ${stage}`.trim();

  const pluralStage = stage.endsWith("s") ? stage : `${stage}s`;
  return String(saint.name || "").includes(pluralStage) ? pluralStage : stage;
}

function getFeastPairs(saint) {
  if (!saint) return [];
  if (Array.isArray(saint.feastKeys) && saint.feastKeys.length) {
    return saint.feastKeys.map(parseFeastKey).filter(Boolean);
  }
  if (Array.isArray(saint.feasts) && saint.feasts.length) {
    return saint.feasts.map(parseFeastKey).filter(Boolean);
  }
  if (saint.feastKey) {
    const p = parseFeastKey(saint.feastKey);
    return p ? [p] : [];
  }
  if (saint.feastMonth && saint.feastDay) {
    const mm = String(Number(saint.feastMonth)).padStart(2, "0");
    const dd = String(Number(saint.feastDay)).padStart(2, "0");
    return [
      {
        month: Number(saint.feastMonth),
        day: Number(saint.feastDay),
        key: `${mm}-${dd}`,
      },
    ];
  }
  return [];
}

function formatFeast(pair) {
  const dt = new Date(Date.UTC(2000, pair.month - 1, pair.day));
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(dt);
}

function cleanName(name) {
  return String(name || "")
    .replace("Saints", "")
    .replace("Saint", "")
    .replace("Blessed", "")
    .replace("Venerable", "")
    .replace("Servant of God", "")
    .replace("Pope", "")
    .trim();
}

function normalizeOuterDoubleQuotes(s) {
  if (s === null || s === undefined) return "";
  let t = String(s).trim();
  if (!t) return t;
  t = t.replace(/^["“”]+/, "");
  t = t.replace(/["“”]+$/, "");
  return `"${t}"`;
}

function getFirstParagraph(text) {
  if (text === null || text === undefined) return "";

  const firstParagraph = String(text)
    .trim()
    .split(/\n\s*\n/)[0]
    ?.replace(/\s*\n+\s*/g, " ")
    .trim();

  return firstParagraph || "";
}

function SaintPreviewPanel({
  saint,
  profileId,
  image,
  feastText,
  categoryChips,
  bioPreview,
  quotesPreview,
  onOpen,
  loadingOverlay = false,
  loadingText = "Loading…",
  positionLabel = "",
}) {
  const linkBtn =
    "inline-flex items-center justify-center gap-2 rounded-xl border border-dulia-line bg-dulia-panel px-4 py-2 text-sm font-medium text-dulia-navy transition hover:bg-dulia-parchment " +
    "dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft dark:text-dulia-parchment dark:hover:bg-dulia-night";

  const showMoreLink =
    "mt-2 inline-flex text-sm font-medium text-dulia-gold underline decoration-dulia-gold/60 underline-offset-4 transition dark:hover:text-dulia-parchment";

  return (
    <div className="relative">
      {loadingOverlay ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[24px] bg-dulia-panel/80 backdrop-blur-sm dark:bg-dulia-night/80">
          <div className="rounded-full border border-dulia-line bg-white/85 px-4 py-2 text-sm font-medium text-dulia-navy shadow-soft dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft dark:text-dulia-parchment">
            {loadingText}
          </div>
        </div>
      ) : null}

      <div
        className={`grid gap-5 rounded-[24px] lg:grid-cols-12 ${loadingOverlay ? "pointer-events-none opacity-70" : ""
          }`}
        role="link"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen?.();
          }
        }}
        aria-label={`View profile for ${saint?.name || "saint"}`}
        title="Open full profile"
      >
        <div className="lg:col-span-5">
          <div className="overflow-hidden rounded-[24px] border border-dulia-line bg-dulia-parchment/45 shadow-soft dark:border-dulia-lineStrong/30 dark:bg-dulia-night">
            {image ? (
              <img
                src={image}
                alt={saint?.name || "Saint"}
                className="h-80 w-full object-cover object-top md:h-[25rem]"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div className="flex h-56 items-center justify-center text-sm text-dulia-navy/60 dark:text-dulia-parchment/60">
                No image yet
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4 lg:col-span-7">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-3">
              {stageLabel(saint) ? (
                <div className="text-sm font-semibold uppercase tracking-[0.16em] text-dulia-nightSoft dark:text-dulia-goldSoft">
                  {stageLabel(saint)}
                </div>
              ) : null}

              <div className="text-3xl font-bold leading-tight text-dulia-ink dark:text-dulia-parchment">
                {cleanName(saint?.name)}
              </div>

              {(saint?.countryLower || saint?.country || feastText) ? (
                <div className="flex flex-wrap gap-2">
                  {saint?.countryLower || saint?.country ? (
                    <InfoPill tone="muted">
                      <CountryFlag saint={saint} />
                    </InfoPill>
                  ) : null}

                  {feastText ? (
                    <InfoPill tone="accent">
                      Feast{feastText.includes(",") ? "s" : ""}: {feastText}
                    </InfoPill>
                  ) : null}
                </div>
              ) : null}

              {categoryChips.length ? (
                <div className="flex flex-wrap gap-2">
                  {categoryChips.map((c, i) => (
                    <InfoPill key={`${c}-${i}`}>{c}</InfoPill>
                  ))}
                </div>
              ) : null}
            </div>

            {positionLabel ? (
              <InfoPill tone="muted" className="whitespace-nowrap">
                {positionLabel}
              </InfoPill>
            ) : null}
          </div>

          <div className="rounded-[24px] border border-dulia-line bg-dulia-parchment/60 p-4 shadow-soft dark:border-dulia-lineStrong/30 dark:bg-dulia-night">
            {bioPreview ? (
              <>
                <div className="text-sm font-semibold uppercase tracking-[0.12em] text-dulia-nightSoft dark:text-dulia-goldSoft">
                  Bio
                </div>
                <p className="mt-2 text-sm leading-7 text-dulia-ink dark:text-dulia-parchment/90">
                  {bioPreview}
                </p>
                <Link
                  to={`/profile/${profileId}`}
                  className={showMoreLink}
                  onClick={(e) => e.stopPropagation()}
                >
                  Show more
                </Link>
              </>
            ) : (
              <div className="text-sm text-dulia-navy/65 dark:text-dulia-parchment/65">
                No bio yet.
              </div>
            )}

            {quotesPreview.length ? (
              <>
                <div className="mt-5 text-sm font-semibold uppercase tracking-[0.12em] text-dulia-nightSoft dark:text-dulia-goldSoft">
                  Quotes
                </div>
                <ul className="mt-3 space-y-3">
                  {quotesPreview.map((q, i) => (
                    <li
                      key={i}
                      className="rounded-2xl border border-dulia-line bg-white/85 p-3 dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft"
                    >
                      <div className="border-l-4 border-dulia-gold pl-3 text-sm leading-6 text-dulia-ink dark:text-dulia-parchment/90">
                        {q}
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2">
            <Link
              to={`/profile/${profileId}`}
              className={linkBtn}
              onClick={(e) => e.stopPropagation()}
            >
              View Full Profile →
            </Link>
            <NavLink
              to="/search"
              className={linkBtn}
              onClick={(e) => e.stopPropagation()}
            >
              <FaSearch aria-hidden="true" />
              Search
            </NavLink>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const showSignInNote = !loading && !user;

  useEffect(() => {
    document.title = "Dulia";
  }, []);

  const siteTitle = "Dulia";
  const siteSubtitle = "A living index of the saints";
  const siteDescription = "Discover and share saints from around the world, across history, and from all walks of life.";
  const canonicalUrl = "https://dulia.us/";
  const siteOrigin = "https://dulia.us";
  const ogImageUrl = `${siteOrigin}/og-image.png`;

  const heroLink =
    "font-semibold text-dulia-gold underline decoration-dulia-gold/60 underline-offset-4 transition hover:text-dulia-parchment";

  const actionLink =
    "inline-flex items-center justify-center gap-2 rounded-xl border border-dulia-line bg-dulia-panel px-4 py-2 text-sm font-medium text-dulia-navy transition hover:bg-dulia-parchment " +
    "dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft dark:text-dulia-parchment dark:hover:bg-dulia-night";

  // const getStartedLink =
  //   "relative isolate inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl border border-dulia-line bg-dulia-panel px-4 py-2 text-sm font-medium text-dulia-navy transition-[color,border-color] duration-300 " +
  //   "[&>*]:relative [&>*]:z-10 " +
  //   "before:absolute before:inset-0 before:z-0 before:opacity-0 before:transition-opacity before:duration-300 " +
  //   "before:bg-[linear-gradient(90deg,rgba(236,202,132,0.95)_0%,rgba(237,204,136,0.95)_12%,rgba(239,208,144,0.95)_24%,rgba(242,214,154,0.96)_36%,rgba(244,220,166,0.96)_46%,rgba(245,223,173,0.96)_50%,rgba(244,220,166,0.96)_54%,rgba(242,214,154,0.96)_64%,rgba(239,208,144,0.95)_76%,rgba(237,204,136,0.95)_88%,rgba(236,202,132,0.95)_100%)] " +
  //   "before:bg-[length:320%_100%] " +
  //   "hover:border-dulia-gold/70 hover:text-dulia-night hover:before:opacity-100 hover:before:animate-[softgold-slide_6s_ease-in-out_infinite] " +
  //   "dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft dark:text-dulia-parchment dark:hover:border-dulia-gold/60 dark:hover:text-dulia-night";

  const getStartedLink =
    "relative isolate inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl border px-4 py-2 text-sm font-medium text-white shadow-soft " +
    "border-white/20 bg-dulia-navy bg-cover bg-center bg-no-repeat transition duration-150 hover:brightness-[0.8] " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-dulia-night " +
    "disabled:cursor-not-allowed disabled:opacity-50";

  const getStartedTextureStyle = {
    backgroundImage: `url(${goldTexture})`,
    backgroundSize: "105% 105%",
  };

  const iconBtn =
    "inline-flex items-center justify-center rounded-xl border border-dulia-line bg-dulia-panel px-3 py-2 text-sm font-medium text-dulia-navy transition hover:bg-dulia-parchment disabled:cursor-not-allowed disabled:opacity-50 " +
    "dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft dark:text-dulia-parchment dark:hover:bg-dulia-night";

  // spotlight state
  const [spotlight, setSpotlight] = useState({
    status: "idle", // idle | loading | ready | error
    saint: null,
    id: null,
    error: "",
  });

  const ranRef = useRef(false);

  // ✅ track last successful id so we can avoid repeating it
  const lastIdRef = useRef(null);

  async function getDifferentRandomId(prevId) {
    // try a few times to avoid re-rolling the same saint
    let last = null;
    for (let i = 0; i < 8; i++) {
      const id = await getRandomSaintId();
      last = id;
      if (!id) return null;
      if (!prevId || id !== prevId) return id;
    }
    // if we couldn't get a different one (tiny DB / bad luck), return the last id
    return last;
  }

  async function loadSpotlight() {
    setSpotlight({ status: "loading", saint: null, id: null, error: "" });

    try {
      const prevId = lastIdRef.current;
      const id = await getDifferentRandomId(prevId);

      if (!id) {
        setSpotlight({
          status: "error",
          saint: null,
          id: null,
          error: "Couldn’t find a random saint. Try again.",
        });
        return;
      }

      const snap = await getDoc(doc(db, "saints", id));
      if (!snap.exists()) {
        setSpotlight({
          status: "error",
          saint: null,
          id: null,
          error: "That profile didn’t load. Try again.",
        });
        return;
      }

      lastIdRef.current = snap.id; // ✅ update last id only on success

      setSpotlight({
        status: "ready",
        saint: { id: snap.id, ...snap.data() },
        id: snap.id,
        error: "",
      });
    } catch {
      setSpotlight({
        status: "error",
        saint: null,
        id: null,
        error: "Something went wrong loading a random saint.",
      });
    }
  }

  // initial load (avoid double-run in React 18 StrictMode dev)
  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    loadSpotlight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const heroImage = useMemo(() => {
    const g = spotlight.saint?.gallery;
    if (!Array.isArray(g) || !g.length) return "";
    return g[0] || "";
  }, [spotlight.saint]);

  const feastText = useMemo(() => {
    const pairs = getFeastPairs(spotlight.saint);
    if (!pairs.length) return "";
    return pairs.length === 1
      ? formatFeast(pairs[0])
      : pairs.map(formatFeast).join(", ");
  }, [spotlight.saint]);

  const categoryChips = useMemo(() => {
    const cats = Array.isArray(spotlight.saint?.categories)
      ? spotlight.saint.categories
      : [];
    const tags = Array.isArray(spotlight.saint?.tags) ? spotlight.saint.tags : [];
    return [...cats.slice(0, 3), ...tags.slice(0, 3)].slice(0, 5);
  }, [spotlight.saint]);

  // ✅ bio + quotes preview
  const bioPreview = useMemo(() => {
    return getFirstParagraph(spotlight.saint?.bio);
  }, [spotlight.saint]);

  const quotesPreview = useMemo(() => {
    const q = spotlight.saint?.quotes;
    if (!Array.isArray(q)) return [];
    return q.filter(Boolean).slice(0, 3).map(normalizeOuterDoubleQuotes);
  }, [spotlight.saint]);

  const goToSpotlight = () => {
    if (!spotlight?.id) return;
    navigate(`/profile/${spotlight.id}`);
  };

  // ----------------------------
  // Favorites carousel
  // ----------------------------
  const userEmail = String(user?.email || "").trim();
  const isSignedIn = !loading && !!userEmail;

  const [favoriteIds, setFavoriteIds] = useState([]);
  const [favIndex, setFavIndex] = useState(0);
  const [favStatus, setFavStatus] = useState("idle"); // idle | loadingIds | loadingProfile | ready | empty | error
  const [favError, setFavError] = useState("");
  const [favSaint, setFavSaint] = useState(null);

  // Fetch user's favorites list
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!isSignedIn) {
        setFavoriteIds([]);
        setFavIndex(0);
        setFavSaint(null);
        setFavStatus("idle");
        setFavError("");
        return;
      }

      setFavStatus("loadingIds");
      setFavError("");
      setFavSaint(null);

      try {
        const snap = await getDoc(doc(db, "users", userEmail));
        if (cancelled) return;

        const favs =
          snap.exists() && Array.isArray(snap.data()?.favorites)
            ? snap.data().favorites.filter(Boolean)
            : [];

        setFavoriteIds(favs);

        if (!favs.length) {
          setFavIndex(0);
          setFavStatus("empty");
          return;
        }

        setFavIndex(0);
        setFavStatus("loadingProfile");
      } catch {
        if (cancelled) return;
        setFavoriteIds([]);
        setFavIndex(0);
        setFavSaint(null);
        setFavStatus("error");
        setFavError("Couldn’t load your favorites.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isSignedIn, userEmail]);

  // Clamp index when list changes
  useEffect(() => {
    if (!favoriteIds.length) return;
    setFavIndex((i) => Math.min(Math.max(0, i), favoriteIds.length - 1));
  }, [favoriteIds]);

  // Load current favorite profile (✅ keep previous favSaint while loading next to avoid UI jump)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!favoriteIds.length) {
        setFavSaint(null);
        return;
      }

      const id = favoriteIds[favIndex];
      if (!id) return;

      setFavStatus("loadingProfile");
      setFavError("");
      // ✅ DO NOT setFavSaint(null) here

      try {
        const snap = await getDoc(doc(db, "saints", id));
        if (cancelled) return;

        if (!snap.exists()) {
          setFavStatus("error");
          setFavError("That favorite profile no longer exists.");
          return;
        }

        const data = snap.data();
        if (data?.approved === false) {
          setFavStatus("error");
          setFavError("That favorite profile is not available right now.");
          return;
        }

        setFavSaint({ id: snap.id, ...data });
        setFavStatus("ready");
      } catch {
        if (cancelled) return;
        setFavStatus("error");
        setFavError("Couldn’t load that favorite profile.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [favoriteIds, favIndex]);

  function prevFavorite() {
    if (!favoriteIds.length) return;
    setFavIndex((i) => (i - 1 + favoriteIds.length) % favoriteIds.length);
  }

  function nextFavorite() {
    if (!favoriteIds.length) return;
    setFavIndex((i) => (i + 1) % favoriteIds.length);
  }

  const favHeroImage = useMemo(() => {
    const g = favSaint?.gallery;
    if (!Array.isArray(g) || !g.length) return "";
    return g[0] || "";
  }, [favSaint]);

  const favFeastText = useMemo(() => {
    const pairs = getFeastPairs(favSaint);
    if (!pairs.length) return "";
    return pairs.length === 1
      ? formatFeast(pairs[0])
      : pairs.map(formatFeast).join(", ");
  }, [favSaint]);

  const favCategoryChips = useMemo(() => {
    const cats = Array.isArray(favSaint?.categories) ? favSaint.categories : [];
    const tags = Array.isArray(favSaint?.tags) ? favSaint.tags : [];
    return [...cats.slice(0, 3), ...tags.slice(0, 3)].slice(0, 5);
  }, [favSaint]);

  const favBioPreview = useMemo(() => {
    return getFirstParagraph(favSaint?.bio);
  }, [favSaint]);

  const favQuotesPreview = useMemo(() => {
    const q = favSaint?.quotes;
    if (!Array.isArray(q)) return [];
    return q.filter(Boolean).slice(0, 2).map(normalizeOuterDoubleQuotes);
  }, [favSaint]);

  const goToFavorite = () => {
    const id = favSaint?.id || favoriteIds[favIndex];
    if (!id) return;
    navigate(`/profile/${id}`);
  };

  return (
    <>
      <Helmet>
        <title>{siteTitle}</title>
        <meta name="description" content={siteDescription} />

        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={siteTitle} />
        <meta property="og:description" content={siteSubtitle} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:site_name" content={siteTitle} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={siteTitle} />
        <meta name="twitter:description" content={siteSubtitle} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>

      <div className="mx-auto max-w-6xl space-y-8 px-4 py-8 md:px-6 lg:space-y-10 lg:py-10">
        <section className="overflow-hidden rounded-[32px] border border-dulia-line bg-dulia-hero text-white shadow-soft dark:border-dulia-lineStrong/30">
          <div className="grid gap-8 px-6 py-8 lg:grid-cols-12 lg:px-8 lg:py-10">
            <div className="space-y-5 lg:col-span-7">
              <div className="flex items-center gap-4">
                <div className="rounded-2xl border border-dulia-goldSoft p-3 backdrop-blur-sm">
                  <img
                    src={duliaLogoGold}
                    alt="Dulia"
                    className="h-12 w-12 object-contain"
                    loading="lazy"
                  />
                </div>
                <div>
                  <h1 className="mt-1 text-4xl font-bold leading-tight sm:text-5xl">
                    Dulia
                  </h1>
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-dulia-goldSoft">
                    A living index of the saints
                  </p>
                </div>
              </div>

              <div className="max-w-2xl space-y-3">
                {/* <p className="text-lg font-semibold text-white/95">
                  A living index of the saints.
                </p> */}
                <p className="text-sm leading-7 text-white/85 sm:text-base">
                  Discover and share saints from around the world, across history, and from all walks of life.
                </p>
                <p className="text-sm leading-7 text-white/85">
                  Learn about this growing project{" "}
                  <Link to="/about" className={heroLink}>
                    here
                  </Link>
                  .
                </p>
                {showSignInNote ? (
                  <p className="rounded-2xl border border-dulia-gold/30 bg-white/10 px-4 py-3 text-sm text-white/90 backdrop-blur-sm">
                    Want to help? Sign in with Google to request new profiles.
                  </p>
                ) : null}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-[28px] border border-white/15 bg-white/10 p-5 shadow-soft backdrop-blur-md">
                <div className="mb-4">
                  <p className="text-sm font-semibold uppercase tracking-[0.16em] text-dulia-goldSoft">
                    Get Started
                  </p>
                  {/* <h2 className="mt-2 text-2xl font-semibold">Get started</h2>
                  <p className="mt-2 text-sm leading-6 text-white/80">
                    Browse the database, or jump right into a random profile. 
                  </p> */}
                </div>

                <div className="grid gap-3">
                  <NavLink
                    to="/search"
                    className={getStartedLink}
                    style={getStartedTextureStyle}
                  >
                    <FaSearch aria-hidden="true" />
                    <span className="relative z-10">Search Profiles</span>
                  </NavLink>

                  <RandomNavButton
                    className={getStartedLink}
                    title="Random"
                    style={getStartedTextureStyle}
                  >
                    <span className="relative z-10">Random Saint</span>
                  </RandomNavButton>

                  <NavLink
                    to="/calendar"
                    className={getStartedLink}
                    style={getStartedTextureStyle}
                  >
                    <FaCalendarAlt aria-hidden="true" />
                    <span className="relative z-10">Liturgical Calendar</span>
                  </NavLink>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-[32px] border border-dulia-line bg-dulia-panel shadow-soft dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft">
          <div className="flex flex-col gap-4 border-b border-dulia-line bg-dulia-parchment/60 px-6 py-5 dark:border-dulia-lineStrong/30 dark:bg-dulia-night lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-dulia-nightSoft dark:text-dulia-goldSoft">
                Saint Spotlight
              </p>
              <h2 className="mt-1 text-2xl font-semibold text-dulia-ink dark:text-dulia-parchment">
                Preview a profile from Dulia&apos;s database, picked just for you
              </h2>
              {/* <p className="mt-2 max-w-2xl text-sm leading-6 text-dulia-navy/75 dark:text-dulia-parchment/75">
                Preview a profile from Dulia&apos;s database, picked just for you.
              </p> */}
            </div>

            <Button
              type="button"
              onClick={loadSpotlight}
              disabled={spotlight.status === "loading"}
              className="whitespace-nowrap gap-2"
            >
              <FaSyncAlt aria-hidden="true" />
              {spotlight.status === "loading" ? "Loading…" : "Try Another"}
            </Button>
          </div>

          <div className="p-6">
            {spotlight.status === "loading" ? (
              <div className="animate-pulse rounded-[24px] border border-dulia-line bg-dulia-parchment/50 p-5 dark:border-dulia-lineStrong/30 dark:bg-dulia-night">
                <div className="h-5 w-40 rounded-full bg-dulia-line/70 dark:bg-dulia-lineStrong/20" />
                <div className="mt-3 h-10 w-2/3 rounded-xl bg-dulia-line/60 dark:bg-dulia-lineStrong/20" />
                <div className="mt-5 h-72 rounded-[20px] bg-dulia-line/60 dark:bg-dulia-lineStrong/20" />
                <div className="mt-5 flex gap-2">
                  <div className="h-7 w-20 rounded-full bg-dulia-line/60 dark:bg-dulia-lineStrong/20" />
                  <div className="h-7 w-24 rounded-full bg-dulia-line/60 dark:bg-dulia-lineStrong/20" />
                  <div className="h-7 w-16 rounded-full bg-dulia-line/60 dark:bg-dulia-lineStrong/20" />
                </div>
              </div>
            ) : spotlight.status === "error" ? (
              <div className="rounded-[24px] border border-dulia-danger/30 bg-red-50 p-5 text-sm text-dulia-danger dark:border-dulia-danger/40 dark:bg-red-950/20">
                {spotlight.error}
                <div className="mt-3">
                  <Button type="button" onClick={loadSpotlight}>
                    Try again
                  </Button>
                </div>
              </div>
            ) : spotlight.status === "ready" && spotlight.saint ? (
              <SaintPreviewPanel
                saint={spotlight.saint}
                profileId={spotlight.id}
                image={heroImage}
                feastText={feastText}
                categoryChips={categoryChips}
                bioPreview={bioPreview}
                quotesPreview={quotesPreview}
                onOpen={goToSpotlight}
              />
            ) : (
              <div className="text-sm text-dulia-navy/70 dark:text-dulia-parchment/70">
                Loading…
              </div>
            )}
          </div>
        </section>

        <section className="overflow-hidden rounded-[32px] border border-dulia-line bg-dulia-panel shadow-soft dark:border-dulia-lineStrong/30 dark:bg-dulia-nightSoft">
          <div className="flex flex-col gap-4 border-b border-dulia-line bg-dulia-parchment/60 px-6 py-5 dark:border-dulia-lineStrong/30 dark:bg-dulia-night lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-dulia-nightSoft dark:text-dulia-goldSoft">
                Favorites
              </p>
              <h2 className="mt-1 text-2xl font-semibold text-dulia-ink dark:text-dulia-parchment">
                Your favorite profiles
              </h2>
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={prevFavorite}
                className={iconBtn}
                disabled={favoriteIds.length <= 1 || favStatus !== "ready"}
                aria-label="Previous favorite"
                title="Previous"
              >
                <FaChevronLeft aria-hidden="true" />
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={nextFavorite}
                className={iconBtn}
                disabled={favoriteIds.length <= 1 || favStatus !== "ready"}
                aria-label="Next favorite"
                title="Next"
              >
                <FaChevronRight aria-hidden="true" />
              </Button>
            </div>
          </div>

          <div className="p-6">
            {!isSignedIn ? (
              <div className="rounded-[24px] border border-dulia-line bg-dulia-parchment/55 p-5 text-sm text-dulia-navy dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment/80">
                Sign in to save profiles as favorites.
              </div>
            ) : favStatus === "loadingIds" || (favStatus === "loadingProfile" && !favSaint) ? (
              <div className="animate-pulse rounded-[24px] border border-dulia-line bg-dulia-parchment/50 p-5 dark:border-dulia-lineStrong/30 dark:bg-dulia-night">
                <div className="h-5 w-32 rounded-full bg-dulia-line/70 dark:bg-dulia-lineStrong/20" />
                <div className="mt-3 h-10 w-2/3 rounded-xl bg-dulia-line/60 dark:bg-dulia-lineStrong/20" />
                <div className="mt-5 h-64 rounded-[20px] bg-dulia-line/60 dark:bg-dulia-lineStrong/20" />
              </div>
            ) : favStatus === "empty" ? (
              <div className="rounded-[24px] border border-dulia-line bg-dulia-parchment/55 p-5 text-sm text-dulia-navy dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment/80">
                You don&apos;t have any favorites yet. Tap the heart on a profile to save it.
                <div className="mt-4 flex flex-wrap gap-2">
                  <NavLink to="/search" className={actionLink}>
                    <FaSearch aria-hidden="true" />
                    Search
                  </NavLink>
                  <RandomNavButton className={actionLink} title="Random">
                    Random
                  </RandomNavButton>
                </div>
              </div>
            ) : favStatus === "error" ? (
              <div className="rounded-[24px] border border-dulia-danger/30 bg-red-50 p-5 text-sm text-dulia-danger dark:border-dulia-danger/40 dark:bg-red-950/20">
                {favError || "Something went wrong."}
              </div>
            ) : favSaint ? (
              <SaintPreviewPanel
                saint={favSaint}
                profileId={favSaint.id}
                image={favHeroImage}
                feastText={favFeastText}
                categoryChips={favCategoryChips}
                bioPreview={favBioPreview}
                quotesPreview={favQuotesPreview}
                onOpen={goToFavorite}
                loadingOverlay={favStatus === "loadingProfile"}
                positionLabel={
                  favoriteIds.length ? `${favIndex + 1} / ${favoriteIds.length}` : ""
                }
              />
            ) : (
              <div className="text-sm text-dulia-navy/70 dark:text-dulia-parchment/70">
                Loading…
              </div>
            )}
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-3">
          <ActionTile
            title="Search Profiles"
            description="Find saints by name, country, feast day, patronage, and more with
              Dulia&apos;s powerful search tool."
          >
            <NavLink to="/search" className={actionLink}>
              <FaSearch aria-hidden="true" />
              Search
            </NavLink>
          </ActionTile>

          <ActionTile
            title="Random Saint"
            description="Meet someone new! Explore a randomly-selected profile from Dulia&apos;s
              extensive database."
          >
            <RandomNavButton className={actionLink} title="Random">
              Random
            </RandomNavButton>
          </ActionTile>

          <ActionTile
            title="Liturgical Calendar"
            description="Discover what's celebrated on any given day, and find saints by their feast days."
          >
            <NavLink to="/calendar" className={actionLink}>
              <FaCalendarAlt aria-hidden="true" />
              Calendar
            </NavLink>
          </ActionTile>
        </section>
      </div>
    </>
  );
}